import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Grid,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { useTranslation } from "react-i18next";
import "../i18n";
import { getRoleLabels, getStoredTutorType } from "../utils/roleTerminology";
import group01 from "../img/groupGallery/group-01.jpeg";
import group02 from "../img/groupGallery/group-02.jpg";
import group03 from "../img/groupGallery/group-03.png";
import group04 from "../img/groupGallery/group-04.jpg";
import group05 from "../img/groupGallery/group-05.png";
import group06 from "../img/groupGallery/group-06.png";
import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  arrayUnion,
  arrayRemove,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

const isValidEmail = (email) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
const GROUP_IMAGE_OPTIONS = [group01, group02, group03, group04, group05, group06];

export default function TeacherNewStudentForm({
  onClose,
  onCreated,
  teacherId,
  teacherSubjectIds = [],
  students,
  subjects,
  groups,
}) {
  const { t } = useTranslation();
  const role = getRoleLabels(t, getStoredTutorType());
  const isCoach = role.isCoach;

  const [studentData, setStudentData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    fullyRegistered: isCoach ? true : false,
    adminValidated: isCoach ? true : false,
    enrollments: [],
    disabled: false,
    entryDate: new Date().toISOString(),
    startDate: new Date().toISOString().slice(0, 10),
    endDate: "",
  });

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  const [localGroups, setLocalGroups] = useState([]);
  const [autoGroupId, setAutoGroupId] = useState("");
  const [autoCreatingGroup, setAutoCreatingGroup] = useState(false);
  const [autoGroupName, setAutoGroupName] = useState("");

  const autoCreatedGroupRef = useRef(false);
  const autoCreateStartedRef = useRef(false);
  const submittedRef = useRef(false);

  const coachClientFullName = useMemo(() => {
    return `${studentData.firstName || ""} ${studentData.lastName || ""}`.trim();
  }, [studentData.firstName, studentData.lastName]);
  const hasCoachFullName = useMemo(() => {
    return !!studentData.firstName.trim() && !!studentData.lastName.trim();
  }, [studentData.firstName, studentData.lastName]);

  const allGroups = useMemo(() => {
    return [...(groups || []), ...(localGroups || [])];
  }, [groups, localGroups]);

  const myGroups = useMemo(() => {
    return (allGroups || []).filter((g) => g.teacherId === teacherId);
  }, [allGroups, teacherId]);

  const allowedSubjects = useMemo(() => {
    const allowedSet = new Set((teacherSubjectIds || []).filter(Boolean));
    return (subjects || []).filter((s) => allowedSet.has(s.id));
  }, [subjects, teacherSubjectIds]);

  const filteredSubjects = useMemo(() => {
    if (!selectedLevel) return [];
    return allowedSubjects.filter((s) => s.level === selectedLevel);
  }, [allowedSubjects, selectedLevel]);

  const filteredGroups = useMemo(() => {
    if (!selectedLevel || !selectedSubject) return [];
    return myGroups.filter((g) => g.level === selectedLevel && g.subjectId === selectedSubject);
  }, [myGroups, selectedLevel, selectedSubject]);

  useEffect(() => {
    if (isCoach) return;
    setSelectedSubject("");
    setSelectedGroup("");
  }, [isCoach, selectedLevel]);

  useEffect(() => {
    if (isCoach) return;
    setSelectedGroup("");
  }, [isCoach, selectedSubject]);

  const getUniqueGroupName = async (baseName) => {
    let candidate = baseName.trim();
    let idx = 2;

    while (true) {
      const q = query(collection(db, "groups"), where("name", "==", candidate));
      const snap = await getDocs(q);
      if (snap.empty) return candidate;

      candidate = `${baseName} (${idx})`;
      idx += 1;
      if (idx > 100) {
        throw new Error("Unable to generate a unique group name");
      }
    }
  };

  const createCoachGroup = async ({ name, level, subjectId }) => {
    const subject = allowedSubjects.find((s) => s.id === subjectId);
    const uniqueName = await getUniqueGroupName(name);
    const randomGroupImage =
      GROUP_IMAGE_OPTIONS[Math.floor(Math.random() * GROUP_IMAGE_OPTIONS.length)] || "";

    const newGroup = {
      name: uniqueName,
      level,
      subjectId,
      teacherId,
      subjectName: subject?.name || "",
      subjectLevel: subject?.level || level || "",
      studentIds: [],
      entryDate: new Date().toISOString(),
      groupImage: randomGroupImage,
      autoCreated: true,
      groupType: "coach_1on1",
    };

    const groupRef = await addDoc(collection(db, "groups"), newGroup);
    await updateDoc(doc(db, "teachers", teacherId), {
      groupIds: arrayUnion(groupRef.id),
    });

    const fullGroup = { id: groupRef.id, ...newGroup };
    setLocalGroups((prev) => [...prev, fullGroup]);
    setAutoGroupId(groupRef.id);
    setAutoGroupName(fullGroup.name);
    setSelectedGroup(groupRef.id);
    autoCreatedGroupRef.current = true;
  };

  const validate = () => {
    if (!studentData.firstName.trim()) return setError(t("First name is required")), false;

    if (!studentData.lastName.trim()) return setError(t("Last name is required")), false;

    if (!studentData.email.trim()) return setError(t("Email is required")), false;
    if (studentData.email !== studentData.email.toLowerCase())
      return setError(t("Email must contain only lowercase letters")), false;
    if (!isValidEmail(studentData.email)) return setError(t("Please enter a valid email")), false;

    if (!studentData.phone.trim()) return setError(t("Phone number is required")), false;

    const isDuplicateEmail = (students || []).some(
      (s) => (s.email || "").toLowerCase() === studentData.email.toLowerCase()
    );
    if (isDuplicateEmail)
      return setError(
        isCoach ? t("A client with this email already exists") : t("A student with this email already exists")
      ), false;

    if (studentData.endDate && studentData.startDate && studentData.endDate < studentData.startDate) {
      return setError(t("End date cannot be before start date")), false;
    }

    if (!studentData.enrollments.length)
      return setError(
        isCoach
          ? t("The client must be enrolled in at least one group")
          : t("The student must be enrolled in at least one group")
      ), false;

    setError("");
    return true;
  };

  const handleAddEnrollment = () => {
    if (!selectedGroup) return setError(t("Please choose a group")), undefined;

    const groupDetails = filteredGroups.find((g) => g.id === selectedGroup);
    if (!groupDetails) return setError(t("Invalid group")), undefined;

    const isAlready = studentData.enrollments.some((e) => e.groupId === selectedGroup);
    if (isAlready)
      return setError(
        isCoach ? t("The client is already enrolled in this group") : t("The student is already enrolled in this group")
      ), undefined;

    setStudentData((prev) => ({
      ...prev,
      enrollments: [
        ...prev.enrollments,
        {
          groupId: selectedGroup,
          groupName: groupDetails.name,
          subjectId: selectedSubject,
          teacherId: teacherId,
          entryDate: new Date().toISOString(),
        },
      ],
    }));

    setSelectedGroup("");
    setError("");
  };

  const handleSubmit = async () => {
    if (!teacherId) {
      setError(t("Unable to detect the teacher account. Please log in again."));
      return;
    }
    if (!validate()) return;

    setSubmitting(true);
    try {
      submittedRef.current = true;

      const newStudentRef = await addDoc(collection(db, "students"), studentData);

      await Promise.all(
        studentData.enrollments.map((enr) =>
          updateDoc(doc(db, "groups", enr.groupId), {
            studentIds: arrayUnion(newStudentRef.id),
          })
        )
      );

      const created = { id: newStudentRef.id, ...studentData };
      onCreated?.(created);

      setStudentData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        fullyRegistered: isCoach ? true : false,
        adminValidated: isCoach ? true : false,
        enrollments: [],
        disabled: false,
        entryDate: new Date().toISOString(),
        startDate: new Date().toISOString().slice(0, 10),
        endDate: "",
      });
      setSelectedLevel("");
      setSelectedSubject("");
      setSelectedGroup("");
      setError("");
      onClose?.();
    } catch (e) {
      submittedRef.current = false;
      console.error("TeacherNewStudentForm submit error:", e);
      setError(t("An error occurred. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const uniqueLevels = useMemo(() => {
    const levels = allowedSubjects.map((s) => s.level).filter(Boolean);
    return [...new Set(levels)];
  }, [allowedSubjects]);

  useEffect(() => {
    if (!selectedLevel && uniqueLevels.length === 1) {
      setSelectedLevel(uniqueLevels[0]);
    }
  }, [uniqueLevels, selectedLevel]);

  useEffect(() => {
    if (selectedLevel && !selectedSubject && filteredSubjects.length === 1) {
      setSelectedSubject(filteredSubjects[0].id);
    }
  }, [selectedLevel, filteredSubjects, selectedSubject]);

  useEffect(() => {
    if (!isCoach) return;
    if (!uniqueLevels.length || selectedLevel) return;
    setSelectedLevel(uniqueLevels[0]);
  }, [isCoach, uniqueLevels, selectedLevel]);

  useEffect(() => {
    if (!isCoach) return;
    if (!selectedLevel || !filteredSubjects.length || selectedSubject) return;
    setSelectedSubject(filteredSubjects[0].id);
  }, [isCoach, selectedLevel, filteredSubjects, selectedSubject]);

  useEffect(() => {
    if (!isCoach) return;
    if (autoCreateStartedRef.current) return;
    if (autoGroupId) return;
    if (!teacherId || !selectedLevel || !selectedSubject) return;
    if (!hasCoachFullName) return;
    autoCreateStartedRef.current = true;
    setAutoCreatingGroup(true);

    (async () => {
      try {
        await createCoachGroup({
          name: coachClientFullName,
          level: selectedLevel,
          subjectId: selectedSubject,
        });
      } catch (e) {
        autoCreateStartedRef.current = false;
        console.error("Coach auto group creation failed:", e);
        setError(t("An error occurred while adding the group."));
      } finally {
        setAutoCreatingGroup(false);
      }
    })();
  }, [
    isCoach,
    autoGroupId,
    teacherId,
    selectedLevel,
    selectedSubject,
    coachClientFullName,
    hasCoachFullName,
    t,
  ]);

  useEffect(() => {
    if (!isCoach || !autoGroupId) return;

    const groupDetails = myGroups.find((g) => g.id === autoGroupId);
    if (!groupDetails) return;

    setSelectedGroup(autoGroupId);
    setStudentData((prev) => {
      const already = (prev.enrollments || []).some((e) => e.groupId === autoGroupId);
      if (already) return prev;

      return {
        ...prev,
        enrollments: [
          ...(prev.enrollments || []),
          {
            groupId: autoGroupId,
            groupName: groupDetails.name,
            subjectId: selectedSubject,
            teacherId,
            entryDate: new Date().toISOString(),
          },
        ],
      };
    });
  }, [isCoach, autoGroupId, myGroups, selectedSubject, teacherId]);

  useEffect(() => {
    if (!isCoach || !autoGroupId) return;
    if (!hasCoachFullName) return;

    const nextName = coachClientFullName.trim();
    if (!nextName || nextName === autoGroupName) return;

    setAutoGroupName(nextName);

    setLocalGroups((prev) =>
      prev.map((g) => (g.id === autoGroupId ? { ...g, name: nextName } : g))
    );

    setStudentData((prev) => ({
      ...prev,
      enrollments: (prev.enrollments || []).map((enr) =>
        enr.groupId === autoGroupId ? { ...enr, groupName: nextName } : enr
      ),
    }));

    updateDoc(doc(db, "groups", autoGroupId), { name: nextName }).catch((e) => {
      console.error("Failed to sync coach group name with client name:", e);
    });
  }, [isCoach, autoGroupId, hasCoachFullName, coachClientFullName, autoGroupName]);

  useEffect(() => {
    return () => {
      if (!autoCreatedGroupRef.current || submittedRef.current || !autoGroupId) return;

      (async () => {
        try {
          await deleteDoc(doc(db, "groups", autoGroupId));
          const teacherRef = doc(db, "teachers", teacherId);
          const teacherSnap = await getDoc(teacherRef);
          if (teacherSnap.exists()) {
            await updateDoc(teacherRef, { groupIds: arrayRemove(autoGroupId) });
          }
        } catch (e) {
          console.error("Failed to cleanup temporary coach group:", e);
        }
      })();
    };
  }, [autoGroupId, teacherId]);

  return (
    <Box>
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
          {isCoach ? t("Add new client") : t("Add new student")}
        </Typography>
      </Box>

      {error && (
        <Typography sx={{ color: "red", display: "flex", justifyContent: "center", mt: 1 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ pb: 2 }}>
        <TextField
          label={t("First name")}
          fullWidth
          value={studentData.firstName}
          onChange={(e) => setStudentData({ ...studentData, firstName: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          label={t("Last name")}
          fullWidth
          value={studentData.lastName}
          onChange={(e) => setStudentData({ ...studentData, lastName: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          label={t("Email")}
          fullWidth
          value={studentData.email}
          onChange={(e) => setStudentData({ ...studentData, email: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          label={t("Phone number")}
          fullWidth
          value={studentData.phone}
          onChange={(e) => setStudentData({ ...studentData, phone: e.target.value })}
          sx={{ mb: 2 }}
        />

        <Grid container spacing={2} sx={{ mb: 2 }} dir="rtl">
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("Start date")}
              type="date"
              fullWidth
              value={studentData.startDate || ""}
              onChange={(e) => setStudentData({ ...studentData, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText={t("Select the subscription start date")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("End date")}
              type="date"
              fullWidth
              value={studentData.endDate || ""}
              onChange={(e) => setStudentData({ ...studentData, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText={t("Select the subscription end date (optional)")}
            />
          </Grid>
        </Grid>

        <FormControl fullWidth sx={{ mb: 1.5 }}>
          <Typography sx={{ mb: 0.5 }}>{t("Level and stream")}</Typography>
          <Select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            disabled={isCoach}
          >
            <MenuItem value="">{t("Choose level")}</MenuItem>
            {uniqueLevels.map((lvl) => (
              <MenuItem key={lvl} value={lvl}>
                {lvl}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 1.5 }}>
          <Typography sx={{ mb: 0.5 }}>{t("Subject")}</Typography>
          <Select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            disabled={!selectedLevel || isCoach}
          >
            <MenuItem value="">{t("Choose subject")}</MenuItem>
            {filteredSubjects.map((sub) => (
              <MenuItem key={sub.id} value={sub.id}>
                {sub.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 1.5 }}>
          <Typography sx={{ mb: 0.5 }}>{role.groupClassLabel}</Typography>
          <Select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            disabled={!selectedLevel || !selectedSubject || (isCoach && !!autoGroupId)}
          >
            <MenuItem value="">{t("Choose group")}</MenuItem>
            {filteredGroups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
            ))}
          </Select>

          {!isCoach && (
            <Button
              variant="contained"
              color="success"
              onClick={handleAddEnrollment}
              sx={{ mt: 2, fontSize: 16 }}
              disabled={!selectedLevel || !selectedSubject || !selectedGroup}
            >
              {t("Add to group")}
            </Button>
          )}
        </FormControl>

        <Alert severity="info">
          <AlertTitle sx={{ fontSize: 16, fontWeight: 900 }}>
            {role.enrollmentsLabel}
          </AlertTitle>
          {studentData.enrollments.length === 0 ? (
            <Typography>{t("No enrollments yet")}</Typography>
          ) : (
            studentData.enrollments.map((enr, idx) => (
              <Typography key={idx}>{`Groupe : ${enr.groupName}`}</Typography>
            ))
          )}
        </Alert>
      </Box>

      <Box
        sx={{
          position: "flex",
          bgcolor: "background.paper",
          borderTop: "1px solid rgba(15, 23, 42, 0.08)",
          boxShadow: "0 -8px 18px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={submitting || studentData.enrollments.length === 0 || autoCreatingGroup}
          sx={{ fontWeight: 700 }}
        >
          {t("Save")}
        </Button>
      </Box>
    </Box>
  );
}
